import { isNonNullAndDefined } from '~/utils'

import type { ComponentPublicInstance, MaybeRefOrGetter } from 'vue'

// TODO better type
export type UpdateFn<T> = () => Promise<T>
export interface TargetVue<T> {
  updateOnServer: UpdateFn<T>
  getChildren?: () => TargetVue<T>[]
  childrenReferences?: Children<T>
  getUpdatePromises: () => ReturnType<UpdateFn<T>>[]
}
type Children<T> = MaybeRefOrGetter<MaybeRefOrGetter<TargetVue<T> | null>[]>
type RequiredDefines<T> = Omit<TargetVue<T>, keyof ComponentPublicInstance>

/**
 * Used on the parents of a profile update chain.
 * You'll probly need {@link useDefineChildUpdate} to make it work.
 *
 * @example
 * const componentArray = toRaw([
 *   BandProfileEditBase,
 *   BandProfileEditSubgenres,
 *   BandProfileEditMisc,
 *   BandProfileEditLinks,
 *   BandProfileEditNews,
 *   BandProfileEditBiography,
 *   // Here goes the components that are using the `useDefineChildUpdate` composable
 * ])
 *
 * // In this case the template will v-for on this array to define it's component references.
 * const childrenReferences = ref<
 *   (null | InstanceType<(typeof componentArray)[number]>)[]
 * >([])
 *
 * const { getUpdatePromises } =
 * // Adding a type generic to the composable is very much recomended.
 *   useGetChildServerUpdates<DeepPartial<Band>>(childrenReferences)
 */
export function useGetChildServerUpdates<T>(refs: Children<T> = []) {
  function getChildren() {
    return toValue(refs).reduce<TargetVue<T>[]>((accumulator, child) => {
      child = toValue(child)
      if (!child) return accumulator
      if (child.childrenReferences) {
        accumulator = accumulator.concat(
          toValue(child.childrenReferences)
            .map(toValue)
            .filter(isNonNullAndDefined),
        )
      }

      accumulator.push(child)
      return accumulator
    }, [])
  }

  function getUpdatePromises() {
    return getChildren().map(({ updateOnServer }) => updateOnServer())
  }

  return {
    getUpdatePromises,
    getChildren,
  } as const
}

/**
 * This composable is a shorthand to define children for the parent component using {@link useGetChildServerUpdates}
 *
 * @example
 * // We'll define some more child references
 * const bandProfileEditPictureReference = ref<null | InstanceType<
 *   typeof BandProfileEditPicture
 * >>(null)
 * const bandProfileEditLocalizationReference = ref<null | InstanceType<
 *   typeof BandProfileEditLocalization
 * >>(null)
 *
 * // For the sake of "me being a creature of laziness", we'll take the same type as for `useGetChildServerUpdates` example
 * defineExpose({
 *   exportValues,
 *   ...useDefineChildUpdate<DeepPartial<Band>>(
 *     async function bandBaseUpdate() {
 *       if (props.band.name === nameFieldValue.value) return {}
 *
 *       const resp = await coreFetch.$patch<Band>(
 *         '/band/band/' + props.band.id + '/',
 *         {
 *           name: nameFieldValue.value,
 *         },
 *       )
 *       return { name: resp.name }
 *     },
 *     // Here we can optionaly add the children's children.
 *     [bandProfileEditPictureReference, bandProfileEditLocalizationReference],
 *   ),
 * })
 */
export function useDefineChildUpdate<T>(
  updateFunction: TargetVue<T>['updateOnServer'],
  addtionalChildrenReferences: Children<T> = ref([]),
) {
  const { getUpdatePromises, getChildren } = useGetChildServerUpdates(
    addtionalChildrenReferences,
  )

  return {
    updateOnServer: updateFunction,
    getUpdatePromises,
    getChildren,
    childrenReferences: addtionalChildrenReferences,
  } satisfies RequiredDefines<T>
}
